import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from "../components/SEO";
import { PAGE_SEO } from "../seo-data";
import { openBookingModal } from "../components/BookingModal";
import { 
  Zap, Users, BarChart3, MessageSquare, Calendar, 
  RefreshCw, Target, TrendingUp, Check, X, Minus
} from 'lucide-react';

const Features = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observerRef.current.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });

    document.querySelectorAll('.reveal:not(.visible)').forEach(el => {
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const features = [
    {
      icon: <Zap className="w-7 h-7" />,
      title: "Custom AI Voice Agents",
      description: "We build AI voice agents that answer calls, qualify leads, handle FAQs, and book appointments — 24/7, sounding natural and on-brand for your business.",
      color: "pink"
    },
    {
      icon: <MessageSquare className="w-7 h-7" />,
      title: "Smart AI Follow-Up",
      description: "Automated follow-up by text, email, and voice — within seconds. Our AI handles objections, answers questions, and stays on leads until they convert or say no.",
      color: "purple"
    },
    {
      icon: <Calendar className="w-7 h-7" />,
      title: "Appointment Booking Pipeline",
      description: "Leads are automatically nurtured and guided to book directly on your calendar. Your schedule fills up while your team focuses on closing.",
      color: "cyan"
    },
    {
      icon: <BarChart3 className="w-7 h-7" />,
      title: "Performance Dashboard",
      description: "Real-time insights into lead flow, conversion rates, and revenue impact. See exactly how your AI systems are performing.",
      color: "pink"
    },
    {
      icon: <Target className="w-7 h-7" />,
      title: "Lead Qualification & Scoring",
      description: "Our AI automatically qualifies incoming leads based on intent, budget, and timeline so your team focuses on the highest-value prospects.",
      color: "purple"
    },
    {
      icon: <Zap className="w-7 h-7" />,
      title: "CRM Integration",
      description: "Seamless sync with HubSpot, Salesforce, GoHighLevel, Jobber, and 50+ other tools your team already uses.",
      color: "cyan"
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: "Multi-Channel Outreach",
      description: "Reach leads where they are — text, email, phone, and voicemail drop. All automated and managed by our team.",
      color: "pink"
    },
    {
      icon: <TrendingUp className="w-7 h-7" />,
      title: "Continuous Optimization",
      description: "Our team continuously optimizes your AI agents, follow-up sequences, and automations. Your results get better every single week.",
      color: "purple"
    }
  ];

  const comparisonData = [
    { feature: "Lead Response Time", skye: "< 60 seconds", manual: "4-24 hours", agency: "1-4 hours" },
    { feature: "Custom AI Agents", skye: "Built for you", manual: "N/A", agency: "Generic templates" },
    { feature: "Follow-up Consistency", skye: "100%", manual: "20-40%", agency: "60-80%" },
    { feature: "Operating Hours", skye: "24/7/365", manual: "Your work hours", agency: "Business hours" },
    { feature: "Appointment Booking", skye: "AI-automated", manual: "You do it", agency: "Manual" },
    { feature: "Time to Results", skye: "30 days", manual: "3-6 months", agency: "2-3 months" },
    { feature: "Ongoing Optimization", skye: true, manual: false, agency: "partial" },
    { feature: "Multi-Channel Automation", skye: true, manual: false, agency: "partial" },
  ];

  const renderComparisonValue = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-green-500" />;
    if (value === false) return <X className="w-5 h-5 text-red-400" />;
    if (value === "partial") return <Minus className="w-5 h-5 text-yellow-500" />;
    return value;
  };

  return (
    <div className="features-page">
      <SEO {...PAGE_SEO.features} />
      {/* Hero */}
      <section className="features-hero">
        <div className="features-hero-bg"></div>
        <div className="container">
          <div className="features-hero-content reveal">
            <div className="eyebrow">The Complete System</div>
            <h1 data-testid="features-hero-title">Your Done-For-You<br /><span className="gradient-text">AI Growth Engine</span></h1>
            <p className="features-hero-sub">
              Custom AI voice agents, chatbots, follow-up systems, and workflow automations — 
              built and managed by our team so you can focus on closing deals.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-grid-section">
        <div className="container">
          <div className="features-grid">
            {features.map((feature, index) => (
              <div 
                key={feature.title} 
                className={`feature-card feature-card-${feature.color} reveal`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`feature-icon-wrap icon-${feature.color}`}>
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="comparison-section">
        <div className="container">
          <div className="comparison-header reveal">
            <div className="eyebrow">Why Choose Skye AI</div>
            <h2 className="section-h">Compare Your Options</h2>
            <p className="section-p" style={{ margin: '0 auto' }}>
              See how Skye AI stacks up against doing it yourself or hiring another marketing agency.
            </p>
          </div>
          
          <div className="comparison-table-wrap reveal">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th></th>
                  <th className="highlight-col">
                    <div className="col-header">
                      <span className="col-badge">Recommended</span>
                      <span className="col-title">Skye AI</span>
                    </div>
                  </th>
                  <th>
                    <div className="col-header">
                      <span className="col-title">DIY / Manual</span>
                    </div>
                  </th>
                  <th>
                    <div className="col-header">
                      <span className="col-title">Other Agencies</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row) => (
                  <tr key={row.feature}>
                    <td className="feature-name">{row.feature}</td>
                    <td className="highlight-col">{renderComparisonValue(row.skye)}</td>
                    <td>{renderComparisonValue(row.manual)}</td>
                    <td>{renderComparisonValue(row.agency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="features-cta">
        <div className="container">
          <div className="features-cta-inner reveal">
            <h2>Ready to Automate Your Business?</h2>
            <p>Book a free AI Growth Planning Call and see exactly how custom AI can scale your operations.</p>
            <button onClick={openBookingModal} className="btn-gradient" data-testid="features-cta-button">
              Book My AI Growth Planning Call →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
